import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowUpRight, ArrowRight, Star, Code2, Smartphone, Cloud, BrainCircuit,
  Sparkles, Compass, Rocket, Target, BookOpen, Eye, Users, CheckCircle2,
} from "lucide-react";
import MagneticButton from "@/components/site/MagneticButton";
import MorphingHeadline from "@/components/site/MorphingHeadline";
import ParticleField from "@/components/site/ParticleField";
import Marquee from "@/components/site/Marquee";
import StatCounter from "@/components/site/StatCounter";
import TiltCard from "@/components/site/TiltCard";
import { Reveal, Stagger, Item } from "@/components/site/Reveal";
import { SERVICES, PROCESS, STATS, TECH_LOGOS } from "@/lib/content";
import { getProjects } from "@/lib/api";

const MOCK_TESTIMONIALS = [
  {
    id: 1,
    quote: "Nexus delivered what three previous vendors couldn't. The migration was flawless and the new platform is a joy to build on.",
    name: "James Okafor",
    role: "CTO",
    company: "FinFlow Inc.",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1758534063829-a72058381e21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3OTUyMjAwNnww&ixlib=rb-4.1.0&q=85",
  },
  {
    id: 2,
    quote: "The AI triage alone has saved lives. We can say that without hyperbole.",
    name: "Dr. Amara Singh",
    role: "Chief Medical Officer",
    company: "MedTrack Health",
    rating: 5,
    avatar: "https://images.pexels.com/photos/36623225/pexels-photo-36623225.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: 3,
    quote: "Black Friday was the smoothest in our company's history. Not a single incident.",
    name: "Rachel Torres",
    role: "VP Engineering",
    company: "ShopStream Retail",
    rating: 5,
    avatar: "https://images.pexels.com/photos/36623225/pexels-photo-36623225.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: 4,
    quote: "Our completion rates went from embarrassing to industry-leading in under a year.",
    name: "Yuki Tanaka",
    role: "CEO",
    company: "LearnLoop",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1765776830139-72b2184dae5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMGRhcmslMjJiYWNrZ3JvdW5kfGVufDB8fHx8MTc3OTUyMjAwNnww&ixlib=rb-4.1.0&q=85",
  },
  {
    id: 5,
    quote: "We went from flying blind to having a crystal ball. The ROI was evident within 90 days.",
    name: "Marcus Webb",
    role: "SVP Operations",
    company: "Logix Manufacturing",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1758534063829-a72058381e21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMGRhcmslMjJiYWNrZ3JvdW5kfGVufDB8fHx8MTc3OTUyMjAwNnww&ixlib=rb-4.1.0&q=85",
  },
  {
    id: 6,
    quote: "The performance improvement was so dramatic our customers thought we'd launched a new product.",
    name: "Nina Kowalski",
    role: "CTO",
    company: "Pulse Analytics",
    rating: 5,
    avatar: "https://images.pexels.com/photos/36623225/pexels-photo-36623225.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

const ICONS = { Code2, Smartphone, Cloud, BrainCircuit, Sparkles, Compass, Rocket, Target, BookOpen, Eye, Users };

export default function Home() {
  const [projects, setProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [tIndex, setTIndex] = useState(0);

  useEffect(() => {
    getProjects().then((data) => setProjects(data.slice(0, 4)));
    setTestimonials(MOCK_TESTIMONIALS);
  }, []);

  useEffect(() => {
    if (testimonials.length < 2) return;
    const id = setInterval(() => setTIndex((p) => (p + 1) % testimonials.length), 5000);
    return () => clearInterval(id);
  }, [testimonials]);

  return (
    <div data-testid="home-page">
      <Hero />
      <TrustMarquee />
      <ServicesGrid />
      <FeaturedProjects projects={projects} />
      <ProcessTimeline />
      <StatsBand />
      <TestimonialsCarousel items={testimonials} index={tIndex} setIndex={setTIndex} />
      <FinalCTA />
    </div>
  );
}

/* =================== HERO =================== */
function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const op = useTransform(scrollYProgress, [0, 1], [1, 0.2]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] pt-32 pb-20 overflow-hidden mesh-bg grain">
      <div className="absolute inset-0">
        <ParticleField density={70} />
      </div>
      <div className="absolute inset-x-0 top-0 h-[420px] pointer-events-none bg-gradient-radial-fade" />

      <motion.div style={{ y, opacity: op }} className="relative mx-auto max-w-7xl px-6 md:px-12 pt-12">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-nexus-cyan font-semibold"
          data-testid="hero-eyebrow"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-nexus-cyan animate-pulse" />
          A 2025 software studio
        </motion.div>

        <h1 className="mt-8 font-display font-bold leading-[0.95] tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-8xl max-w-5xl">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="block"
          >
            We build software
          </motion.span>
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="block"
          >
            that <MorphingHeadline words={["scales businesses.", "ships fast.", "earns trust.", "compounds."]} />
          </motion.span>
        </h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="mt-8 max-w-2xl text-lg md:text-xl text-zinc-400 leading-relaxed"
        >
          From AI-powered products to cloud-native platforms — we partner with founders and operators
          to design, build, and scale the systems that move their businesses forward.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.55 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <MagneticButton
            as={Link}
            to="/contact"
            data-testid="hero-cta-primary"
            className="group relative inline-flex items-center gap-2 rounded-full bg-nexus-cyan text-nexus-ink font-semibold px-7 py-4 text-sm hover:brightness-110 transition glow-cyan"
          >
            Start your project
            <ArrowUpRight className="h-4 w-4 transition group-hover:rotate-45" />
          </MagneticButton>
          <MagneticButton
            as={Link}
            to="/projects"
            data-testid="hero-cta-secondary"
            className="group inline-flex items-center gap-2 rounded-full glass border-white/10 px-7 py-4 text-sm font-medium hover:bg-white/[0.06] transition"
          >
            View case studies
            <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
          </MagneticButton>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-12 flex flex-wrap items-center gap-6"
          data-testid="hero-trust"
        >
          <div className="flex items-center gap-3">
            <div className="flex">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="h-4 w-4 fill-nexus-cyan text-nexus-cyan" />
              ))}
            </div>
            <span className="font-mono text-xs tracking-wider text-zinc-400">
              4.9/5 · 150+ verified reviews
            </span>
          </div>
        </motion.div>
      </motion.div>

      {/* Floating orb decorations */}
      <div className="pointer-events-none absolute -right-32 top-1/4 h-96 w-96 rounded-full bg-nexus-indigo/20 blur-[120px]" />
      <div className="pointer-events-none absolute -left-32 bottom-0 h-80 w-80 rounded-full bg-nexus-cyan/15 blur-[120px]" />

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">scroll</div>
        <div className="mx-auto mt-2 h-8 w-px bg-gradient-to-b from-nexus-cyan to-transparent animate-pulse" />
      </motion.div>
    </section>
  );
}

/* =================== TRUST MARQUEE =================== */
function TrustMarquee() {
  return (
    <section data-testid="trust-marquee" className="relative py-12 border-y border-white/[0.06]">
      <div className="mx-auto max-w-7xl px-6 md:px-12 mb-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500 text-center">
          Built with industry-leading technologies
        </p>
      </div>
      <Marquee items={TECH_LOGOS.slice(0, 18)} direction="left" className="mb-4" />
      <Marquee items={TECH_LOGOS.slice(14)} direction="right" />
    </section>
  );
}

/* =================== SERVICES =================== */
function ServicesGrid() {
  return (
    <section data-testid="services-section" className="relative py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Reveal>
          <div className="max-w-3xl">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">
              [ 01 — Capabilities ]
            </p>
            <h2 className="mt-5 font-display text-4xl md:text-6xl tracking-tight leading-[1.05]">
              One studio, the full <span className="text-gradient">stack of disciplines</span>.
            </h2>
            <p className="mt-6 text-lg text-zinc-400 max-w-2xl">
              Engineering, design, AI, cloud, and strategy — under one roof, with shared rituals and
              a deeply opinionated taste for shipping.
            </p>
          </div>
        </Reveal>

        <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5" step={0.06}>
          {SERVICES.map((s, idx) => {
            const Icon = ICONS[s.icon] || Code2;
            return (
              <Item key={s.slug} className={s.span}>
                <TiltCard className="group h-full">
                  <div
                    data-testid={`service-card-${s.slug}`}
                    className="relative h-full rounded-2xl glass border-white/[0.08] p-8 hover:bg-white/[0.04] transition overflow-hidden"
                  >
                    <div className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-nexus-cyan/60 to-transparent opacity-0 group-hover:opacity-100 transition" />
                    <div className="flex items-start justify-between mb-12">
                      <div className="h-12 w-12 grid place-items-center rounded-xl bg-gradient-to-br from-white/5 to-transparent border border-white/10 group-hover:border-nexus-cyan/40 transition">
                        <Icon className="h-5 w-5 text-nexus-cyan" />
                      </div>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                        / {s.tag}
                      </span>
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl tracking-tight">{s.name}</h3>
                    <p className="mt-3 text-zinc-400 leading-relaxed">{s.short}</p>
                    <ul className="mt-6 space-y-2">
                      {s.features.map((f) => (
                        <li key={f} className="flex items-center gap-2 text-sm text-zinc-400">
                          <CheckCircle2 className="h-3.5 w-3.5 text-nexus-cyan/80 shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/services"
                      data-testid={`service-link-${s.slug}`}
                      className="mt-7 inline-flex items-center gap-1.5 text-sm font-medium text-foreground group/link"
                    >
                      Learn more
                      <ArrowUpRight className="h-4 w-4 transition group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                    </Link>
                  </div>
                </TiltCard>
              </Item>
            );
          })}
        </Stagger>
      </div>
    </section>
  );
}

/* =================== FEATURED PROJECTS (horizontal scroll) =================== */
function FeaturedProjects({ projects }) {
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: targetRef });
  const x = useTransform(scrollYProgress, [0, 1], ["1%", "-72%"]);

  if (!projects?.length) {
    return (
      <section className="py-32 mx-auto max-w-7xl px-6 md:px-12">
        <div className="rounded-3xl glass border-white/10 p-12 text-center text-zinc-400">
          Loading featured projects…
        </div>
      </section>
    );
  }

  return (
    <section ref={targetRef} data-testid="featured-projects" className="relative h-[300vh] hidden md:block">
      <div className="sticky top-0 h-screen flex flex-col overflow-hidden">
        <div className="mx-auto max-w-7xl w-full px-6 md:px-12 pt-32 pb-10">
          <div className="flex items-end justify-between gap-6">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">
                [ 02 — Work ]
              </p>
              <h2 className="mt-5 font-display text-4xl md:text-6xl tracking-tight leading-[1.05] max-w-3xl">
                Projects that <span className="text-gradient">made impact</span>.
              </h2>
            </div>
            <Link
              to="/projects"
              data-testid="featured-projects-view-all"
              className="hidden md:inline-flex shrink-0 items-center gap-1.5 rounded-full glass border-white/10 px-5 py-2.5 text-sm hover:bg-white/[0.05] transition"
            >
              View all <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        <motion.div style={{ x }} className="flex gap-6 px-12 will-change-transform">
          {projects.map((p) => (
            <Link
              to={`/projects/${p.slug}`}
              key={p.id}
              data-testid={`featured-project-${p.slug}`}
              className="group relative shrink-0 w-[78vw] max-w-[860px] aspect-[16/10] rounded-3xl overflow-hidden border border-white/10"
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition duration-700 group-hover:scale-105"
                style={{ backgroundImage: `url(${p.image})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-nexus-ink via-nexus-ink/30 to-transparent" />
              <div className="absolute inset-0 p-10 flex flex-col justify-between">
                <div className="flex items-start justify-between gap-6">
                  <div className="flex flex-wrap items-center gap-2">
                    {p.industry.map((i) => (
                      <span key={i} className="rounded-full glass px-3 py-1 text-[11px] uppercase tracking-wider text-zinc-300">
                        {i}
                      </span>
                    ))}
                  </div>
                  <ArrowUpRight className="h-7 w-7 text-nexus-cyan opacity-0 group-hover:opacity-100 transition" />
                </div>
                <div>
                  <h3 className="font-display text-3xl md:text-4xl tracking-tight max-w-xl">{p.title}</h3>
                  <p className="mt-3 text-zinc-300 max-w-xl">{p.summary}</p>
                  <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-3">
                    {(p.metrics || []).slice(0, 3).map((m) => (
                      <div key={m.label}>
                        <div className="font-display text-3xl tracking-tight text-gradient-cyan">{m.value}</div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 mt-1">
                          {m.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* Mobile horizontal-snap fallback handled inside ProjectsMobile below */

/* =================== PROCESS =================== */
function ProcessTimeline() {
  return (
    <section data-testid="process-section" className="relative py-32 border-t border-white/[0.06]">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Reveal>
          <div className="max-w-3xl">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">
              [ 03 — Process ]
            </p>
            <h2 className="mt-5 font-display text-4xl md:text-6xl tracking-tight leading-[1.05]">
              A method that <span className="text-gradient">ships</span>.
            </h2>
            <p className="mt-6 text-lg text-zinc-400 max-w-2xl">
              Five phases, weekly demos, and clear ownership at every step. No surprises, ever.
            </p>
          </div>
        </Reveal>

        <Stagger className="mt-16 grid md:grid-cols-5 gap-5" step={0.06}>
          {PROCESS.map((p, i) => (
            <Item key={p.step}>
              <div className="relative h-full rounded-2xl glass border-white/[0.08] p-7 hover:bg-white/[0.04] transition">
                <div className="flex items-center justify-between mb-8">
                  <span className="font-mono text-xs text-zinc-500">{p.step}</span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-nexus-cyan">{p.duration}</span>
                </div>
                <h3 className="font-display text-2xl tracking-tight">{p.title}</h3>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{p.body}</p>
                {i < PROCESS.length - 1 && (
                  <div className="absolute top-1/2 -right-3 hidden md:block">
                    <ArrowRight className="h-4 w-4 text-zinc-700" />
                  </div>
                )}
              </div>
            </Item>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

/* =================== STATS =================== */
function StatsBand() {
  return (
    <section data-testid="stats-section" className="relative py-24 border-y border-white/[0.06] mesh-bg">
      <div className="mx-auto max-w-7xl px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-8">
        {STATS.map((s) => (
          <Reveal key={s.label}>
            <div className="text-center md:text-left" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="font-display text-5xl md:text-7xl tracking-tighter text-gradient">
                <StatCounter value={s.value} suffix={s.suffix} />
              </div>
              <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                {s.label}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* =================== TESTIMONIALS =================== */
function TestimonialsCarousel({ items, index, setIndex }) {
  if (!items?.length) return null;
  const current = items[index];
  return (
    <section data-testid="testimonials-section" className="relative py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 max-w-4xl">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">
                [ 04 — Voices ]
              </p>
              <h2 className="mt-5 font-display text-4xl md:text-6xl tracking-tight leading-[1.05]">
                What partners <span className="text-gradient">say</span>.
              </h2>
            </div>
          </div>
        </Reveal>

        <div className="mt-16 grid md:grid-cols-12 gap-6 items-stretch">
          <motion.blockquote
            key={current.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="md:col-span-8 rounded-3xl glass border-white/[0.08] p-10 md:p-14"
            data-testid="testimonial-current"
          >
            <div className="flex items-center gap-1 mb-6">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className={`h-4 w-4 ${i < current.rating ? "fill-nexus-cyan text-nexus-cyan" : "text-zinc-700"}`} />
              ))}
            </div>
            <p className="font-display text-2xl md:text-4xl leading-[1.2] tracking-tight">
              "{current.quote}"
            </p>
            <div className="mt-8 flex items-center gap-4">
              <img src={current.avatar} alt={current.name} className="h-12 w-12 rounded-full object-cover border border-white/10" />
              <div>
                <div className="font-medium">{current.name}</div>
                <div className="text-sm text-zinc-500">{current.role} · {current.company}</div>
              </div>
            </div>
          </motion.blockquote>

          <div className="md:col-span-4 grid grid-cols-3 md:grid-cols-1 gap-3 content-start">
            {items.slice(0, 6).map((t, i) => (
              <button
                key={t.id}
                data-testid={`testimonial-dot-${i}`}
                onClick={() => setIndex(i)}
                className={`group rounded-2xl glass border-white/[0.08] p-4 text-left transition hover:bg-white/[0.05] ${i === index ? "ring-1 ring-nexus-cyan/60" : ""}`}
              >
                <div className="flex items-center gap-3">
                  <img src={t.avatar} alt={t.name} className="h-10 w-10 rounded-full object-cover border border-white/10" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{t.name}</div>
                    <div className="text-[11px] font-mono text-zinc-500 truncate">{t.company}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* =================== FINAL CTA =================== */
function FinalCTA() {
  return (
    <section data-testid="final-cta-section" className="relative py-32 border-t border-white/[0.06]">
      <div className="mx-auto max-w-5xl px-6 md:px-12">
        <div className="relative rounded-[2rem] glass border-white/[0.08] p-12 md:p-20 overflow-hidden">
          <div className="absolute -top-32 -right-32 h-80 w-80 rounded-full bg-nexus-cyan/20 blur-[120px]" />
          <div className="absolute -bottom-32 -left-32 h-80 w-80 rounded-full bg-nexus-indigo/20 blur-[120px]" />
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold relative">
            [ Let's talk ]
          </p>
          <h2 className="mt-5 font-display text-4xl md:text-6xl tracking-tight leading-[1.05] max-w-3xl relative">
            Ready to <span className="text-gradient">transform</span> your business?
          </h2>
          <p className="mt-6 text-lg text-zinc-400 max-w-2xl relative">
            Let's discuss your project and explore how we can help you reach the next milestone.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4 relative">
            <MagneticButton
              as={Link}
              to="/contact"
              data-testid="final-cta-primary"
              className="group inline-flex items-center gap-2 rounded-full bg-nexus-cyan text-nexus-ink font-semibold px-7 py-4 text-sm hover:brightness-110 transition glow-cyan"
            >
              Schedule a consultation
              <ArrowUpRight className="h-4 w-4 transition group-hover:rotate-45" />
            </MagneticButton>
            <a
              href="mailto:admin@fullstackendeavours.in"
              data-testid="final-cta-email"
              className="inline-flex items-center gap-2 rounded-full glass border-white/10 px-7 py-4 text-sm font-medium hover:bg-white/[0.06] transition"
            >
              admin@fullstackendeavours.in
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
